// src/components/projects/ChatWindow/ModelSelectorPopover.js

import React from 'react';
import {
  Popover,
  Box,
  Typography,
  Chip,
  CircularProgress,
  ListItemButton,
  IconButton,
  Button,
  alpha,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  Add,
  MoreVert,
  AutoAwesome,
  Psychology,
  DataObject,
  Rocket,
  Cloud,
  Code,
  Lock,
  CheckCircle,
} from '@mui/icons-material';
import { AIDisplayIcon } from '../../components/styled/CustomIcons';

const ModelSelectorPopover = ({
  anchorEl,
  onClose,
  availableModels,
  isLoadingModels,
  selectedModel,
  onModelSelect,
  onConfigure,
  onReconfigure,
  onRefresh,
  onAddModel,
  onActionMenuOpen,
  isFloxpadModel,
  failedModels,
  currentCompanyId,
  userDetail,
}) => {
  const theme = useTheme();

  const getModelStatus = (model) => {
    const companyId = currentCompanyId || userDetail?.companyId || 1;
    const isFailed = failedModels[model.id || model.model || model.modelCode] || false;
    const isOpen = (model.credentialStatus === 'OPEN' || model.status === 'OPEN') &&
      (model.companyId === companyId || !model.companyId) &&
      !isFailed;
    const isLocked = !isOpen && !isFloxpadModel(model) && !isFailed;
    const needsReconfig = isFailed && !isFloxpadModel(model);

    return { isOpen, isLocked, needsReconfig, isFailed };
  };

  const getModelIcon = (model) => {
    const provider = model?.provider?.toLowerCase() || '';
    const modelCode = model?.modelCode?.toLowerCase() || '';

    // Floxpad AI - Use the AIDisplayIcon
    if (provider === 'floxpad' || model?.builtIn || modelCode.includes('floxpad')) {
      return <AIDisplayIcon size="small" animated={false} />;
    }
    // Provider-specific icons
    if (provider === 'anthropic' || modelCode.includes('claude')) {
      return <AutoAwesome sx={{ fontSize: 16 }} />;
    }
    if (provider === 'openai' || modelCode.includes('gpt')) {
      return <Psychology sx={{ fontSize: 16 }} />;
    }
    if (provider === 'gemini' || modelCode.includes('gemini')) {
      return <Rocket sx={{ fontSize: 16 }} />;
    }
    if (provider === 'deepseek' || modelCode.includes('deepseek')) {
      return <DataObject sx={{ fontSize: 16 }} />;
    }
    if (provider === 'grok' || modelCode.includes('grok')) {
      return <Cloud sx={{ fontSize: 16 }} />;
    }
    if (provider === 'azure-openai' || modelCode.includes('azure')) {
      return <Code sx={{ fontSize: 16 }} />;
    }

    return <AutoAwesome sx={{ fontSize: 16 }} />;
  };

  const getIconColor = (model) => {
    const provider = model?.provider?.toLowerCase() || '';
    const modelCode = model?.modelCode?.toLowerCase() || '';

    if (provider === 'floxpad' || model?.builtIn || modelCode.includes('floxpad')) {
      return theme.palette.info.main;
    }
    if (provider === 'anthropic' || modelCode.includes('claude')) {
      return theme.palette.error.main;
    }
    if (provider === 'openai' || modelCode.includes('gpt')) {
      return theme.palette.success.main;
    }
    if (provider === 'gemini' || modelCode.includes('gemini')) {
      return theme.palette.info.main;
    }
    if (provider === 'deepseek' || modelCode.includes('deepseek')) {
      return theme.palette.warning.main;
    }
    if (provider === 'grok' || modelCode.includes('grok')) {
      return theme.palette.secondary.main;
    }
    if (provider === 'azure-openai' || modelCode.includes('azure')) {
      return theme.palette.primary.main;
    }

    return theme.palette.grey[500];
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          width: 340,
          maxHeight: 480,
          borderRadius: 2,
          boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          overflow: "hidden",
          border: '1px solid rgba(0,0,0,0.06)',
          backgroundColor: theme.palette.background.paper,
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        pb: 1.5, 
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" fontWeight={600} fontSize="0.85rem">
          Choose Model
        </Typography>
      </Box>

      {/* Model List */}
      <Box sx={{ p: 1, overflowY: "auto", maxHeight: 360 }}>
        {isLoadingModels ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={24} />
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Loading models...
            </Typography>
          </Box>
        ) : availableModels.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary" fontSize="0.85rem">
              No models available
            </Typography>
          </Box>
        ) : (
          availableModels.map((model) => {
            const isFloxpad = isFloxpadModel(model);
            const { isOpen, isLocked, needsReconfig } = getModelStatus(model);

            const displayName = model.label || model.name || model.model || model.modelCode || 'Unknown Model';
            const description = model.description || model.model || '';
            const provider = model.provider || '';

            const isSelected = selectedModel?.model === model.model ||
              selectedModel?.id === model.id ||
              selectedModel?.modelCode === model.modelCode;

            const icon = getModelIcon(model);
            const iconColor = getIconColor(model);

            // Only show green dot if model is selected AND configured/Open
            const showGreenDot = isSelected && (isOpen || isFloxpad);

            return (
              <Tooltip
                key={model.model || model.id || model.modelCode}
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#fff' }}>
                      {displayName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      {description || `${provider || 'Model'} configuration`}
                    </Typography>
                    {provider && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mt: 0.5 }}>
                        Provider: {provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </Typography>
                    )}
                  </Box>
                }
                placement="left"
                arrow
                enterDelay={300}
                leaveDelay={100}
              >
                <Box
                  sx={{
                    mb: 0.5,
                    borderRadius: 1.5,
                    backgroundColor: 'transparent',
                    transition: 'all 0.15s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <ListItemButton
                    onClick={() => {
                      if (isLocked) {
                        onConfigure(model);
                        return;
                      }
                      if (needsReconfig) {
                        onReconfigure(model);
                        return;
                      }
                      onModelSelect(model);
                    }}
                    sx={{
                      borderRadius: 1.5,
                      py: 1.2,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      {/* Model Icon */}
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          backgroundColor: alpha(iconColor, 0.08),
                          color: iconColor,
                        }}
                      >
                        {icon}
                      </Box>

                      {/* Model Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            fontWeight={isSelected ? 600 : 500}
                            fontSize="0.8rem"
                            sx={{
                              color: isLocked || needsReconfig ? theme.palette.text.secondary : theme.palette.text.primary,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: '120px',
                            }}
                          >
                            {displayName}
                          </Typography>

                          {isFloxpad && (
                            <Chip
                              label="Default"
                              size="small"
                              sx={{
                                height: 14,
                                fontSize: '0.4rem',
                                fontWeight: 600,
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }}
                            />
                          )}

                          {needsReconfig && (
                            <Chip
                              label="Invalid"
                              size="small"
                              sx={{
                                height: 14,
                                fontSize: '0.4rem',
                                fontWeight: 600,
                                backgroundColor: theme.palette.warning.main,
                                color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }}
                            />
                          )}
                        </Box>

                        {/* <Typography
                          variant="caption"
                          color="textSecondary"
                          fontSize="0.65rem"
                          sx={{
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '150px',
                          }}
                        >
                          {provider ? `${provider.charAt(0).toUpperCase() + provider.slice(1)} model` : description}
                          {isFloxpad ? ' - Always available' : ''}
                          {needsReconfig ? ' - Credentials failed' : ''}
                        </Typography> */}
                      </Box>

                      {/* Status Indicator */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {/* Green dot - only for selected AND configured models */}
                        {showGreenDot && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.success.main,
                              boxShadow: `0 0 8px ${alpha(theme.palette.success.main, 0.4)}`,
                            }}
                          />
                        )}

                        {/* Lock icon for locked models */}
                        {isLocked && !isFloxpad && (
                          <Lock sx={{ fontSize: 14, color: theme.palette.grey[500] }} />
                        )}

                        {/* Status Chip */}
                        <Chip
                          label={isFloxpad ? "Active" : (needsReconfig ? "Invalid" : (isOpen ? "Active" : ""))}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.5rem',
                            fontWeight: 500,
                            borderRadius: '3px',
                            backgroundColor: isFloxpad || isOpen
                              ? alpha(theme.palette.success.main, 0.08)
                              : needsReconfig
                                ? alpha(theme.palette.warning.main, 0.08)
                                : 'transparent',
                            color: isFloxpad || isOpen
                              ? theme.palette.success.main
                              : needsReconfig
                                ? theme.palette.warning.main
                                : theme.palette.grey[500],
                            '& .MuiChip-label': { px: 0.8 },
                            display: isLocked ? 'none' : 'flex',
                          }}
                        />

                        {isOpen && !isFloxpad && (
                          <IconButton
                            size="small"
                            onClick={(e) => onActionMenuOpen(e, model)}
                            sx={{
                              padding: '2px',
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                              }
                            }}
                          >
                            <MoreVert sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {/* Configure button for Locked models */}
                    {isLocked && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.3,
                        ml: 4,
                        pt: 0.3,
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      }}>
                        <Button
                          size="small"
                          variant="text"
                          sx={{
                            fontSize: '0.55rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: 'auto',
                            p: '1px 8px',
                            color: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onConfigure(model);
                          }}
                        >
                          Configure
                        </Button>
                      </Box>
                    )}

                    {/* Update button for Invalid models */}
                    {needsReconfig && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 0.3,
                        ml: 4,
                        pt: 0.3,
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                      }}>
                        <Typography
                          variant="caption"
                          color="warning.main"
                          fontSize="0.6rem"
                        >
                           Credentials validation failed
                        </Typography>
                        <Button
                          size="small"
                          variant="text"
                          sx={{
                            fontSize: '0.55rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: 'auto',
                            p: '1px 8px',
                            color: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onReconfigure(model);
                          }}
                        >
                          Update API Key
                        </Button>
                      </Box>
                    )}
                  </ListItemButton>
                </Box>
              </Tooltip>
            );
          })
        )}
      </Box>

      {/* Footer */}
      <Box sx={{
        p: 1.2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.01),
      }}>
        <Button
          size="small"
          onClick={onRefresh}
          sx={{
            textTransform: 'none',
            fontSize: '0.65rem',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            minWidth: 'auto',
            p: '2px 8px',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              color: theme.palette.primary.main,
            }
          }}
          startIcon={<Refresh sx={{ fontSize: 14 }} />}
        >
          Refresh
        </Button>

      </Box>
    </Popover>
  );
};

export default ModelSelectorPopover;